import { Job, SupabaseJob } from '@applicator/shared'

import JobDateChips from '../JobDateChips'

import { prepareInitialJobDates } from './dateHandlers'
import { formatTimeAgo, formatFullDateTime } from './formatting'
import { getRelevanceStatus, getRelevanceBadgeClasses, getRelevanceBadgeText } from './relevanceHelpers'
import { extractUrlFromDescription, getDomainFromUrl } from './urlHelpers'


// Helper function to render saved at and URL info
export function renderSavedAtAndUrl(
  currentJob: Job | null,
  savedAtRef: React.RefObject<HTMLSpanElement>,
  setSavedAtTooltipPos: React.Dispatch<React.SetStateAction<{ top: number; left: number } | null>>,
  setSavedAtTooltipText: React.Dispatch<React.SetStateAction<string>>,
  setShowSavedAtTooltip: React.Dispatch<React.SetStateAction<boolean>>
) {
  if (!currentJob) return null
  
  const jobUrl = extractUrlFromDescription(currentJob?.description)
  if (!currentJob?.savedAt && !jobUrl) return null
  
  const timeAgoResult = currentJob?.savedAt ? formatTimeAgo(currentJob.savedAt) : ''
  const fullDateTime = currentJob?.savedAt ? formatFullDateTime(currentJob.savedAt) : ''
  
  return (
    <>
      <span className="text-xs text-text-muted">•</span>
      <span 
        ref={savedAtRef}
        className="text-xs text-text-muted cursor-help"
        onMouseEnter={() => {
          if (savedAtRef.current && fullDateTime) {
            const rect = savedAtRef.current.getBoundingClientRect()
            setSavedAtTooltipPos({
              top: rect.top - 8,
              left: rect.left
            })
            setSavedAtTooltipText(fullDateTime)
            setShowSavedAtTooltip(true)
          }
        }}
        onMouseLeave={() => {
          setShowSavedAtTooltip(false)
          setSavedAtTooltipPos(null)
        }}
        onBlur={() => {
          setShowSavedAtTooltip(false)
          setSavedAtTooltipPos(null)
        }}
      >
        Saved {timeAgoResult}
      </span>
      {jobUrl && (
        <>
          <span className="text-xs text-text-muted">•</span>
          <a
            href={jobUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-text-muted hover:text-text-secondary underline"
          >
            {getDomainFromUrl(jobUrl)}
          </a>
        </>
      )}
    </>
  )
}

// Helper function to render relevancy section for search queries
export function renderRelevancySection(
  searchQueryId: string | null,
  currentJob: Job | null,
  supabaseJobs: SupabaseJob[],
  showRelevanceReasoning: boolean,
  setShowRelevanceReasoning: React.Dispatch<React.SetStateAction<boolean>>,
  handleAddToJobs: () => void,
  handleRejectRelevant: () => void
) {
  if (!searchQueryId || !currentJob?.id) return null
  
  const supabaseJob = supabaseJobs.find(j => j.id === currentJob.id)
  if (!supabaseJob) return null
  
  const { isRelevant, isRejected, isNotRelevant, isPending } = getRelevanceStatus(supabaseJob)
  
  return (
    <div className="mb-2">
      {/* Compact relevancy badge + Start Applying button in one row */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Relevancy badge */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`px-2 py-0.5 rounded text-xs font-semibold whitespace-nowrap ${getRelevanceBadgeClasses(isRelevant, isNotRelevant)}`}>
            {getRelevanceBadgeText(isRelevant, isNotRelevant)}
          </span>
          {supabaseJob.relevance_score !== null && (
            <span className="text-xs text-text-secondary whitespace-nowrap">
              {supabaseJob.relevance_score}/100
            </span>
          )}
        </div>
        
        {/* Compact Start Applying button - only for relevant jobs */}
        {isRelevant && !isRejected && (
          <button
            onClick={handleAddToJobs}
            className="px-2.5 py-1 bg-brand-primary text-text-inverse rounded text-xs font-medium hover:bg-brand-primary-hover focus:outline-none focus:ring-2 focus:ring-interactive-focus-ring focus:ring-offset-1 transition-colors flex items-center gap-1 flex-shrink-0"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
            <span>Start Applying</span>
          </button>
        )}
        {isRelevant && !isRejected && (
          <button
            onClick={handleRejectRelevant}
            className="px-2.5 py-1 border border-danger/30 text-danger rounded text-xs font-medium hover:bg-danger-subtle focus:outline-none focus:ring-2 focus:ring-danger focus:ring-offset-1 transition-colors flex items-center gap-1 flex-shrink-0"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span>Reject</span>
          </button>
        )}
        
        {/* Collapsible reasoning toggle */}
        {supabaseJob.relevance_reasoning && (
          <button
            onClick={() => setShowRelevanceReasoning(!showRelevanceReasoning)}
            className="px-2 py-0.5 text-xs text-text-muted hover:text-text-secondary flex items-center gap-1 flex-shrink-0"
          >
            <svg
              className={`w-3 h-3 transition-transform ${showRelevanceReasoning ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <span>Reasoning</span>
          </button>
        )}
      </div>
      
      {/* Collapsible reasoning */}
      {supabaseJob.relevance_reasoning && showRelevanceReasoning && (
        <p className="text-xs text-text-secondary leading-relaxed mt-1.5">
          {supabaseJob.relevance_reasoning}
        </p>
      )}
      {isPending && (
        <p className="text-xs text-text-muted italic mt-1">
          AI analysis in progress...
        </p>
      )}
    </div>
  )
}

// Helper function to render date chips section
export function renderDateChipsSection(
  currentJob: Job | null,
  supabaseJobs: SupabaseJob[],
  userRules: { follow_up_days_after_applied?: number | null; deadline_days_after_saved?: number | null },
  handleDateChipsUpdate: (updatedDates: Record<string, unknown>) => Promise<void>
) {
  if (!currentJob?.id || supabaseJobs.length === 0) return null
  
  const supabaseJob = supabaseJobs.find(j => j.id === currentJob.id)
  if (!supabaseJob) return null
  
  return (
    <div className="mb-2">
      <JobDateChips
        jobId={currentJob.id}
        layout="horizontal"
        initialJobDates={prepareInitialJobDates(supabaseJob)}
        userRules={userRules}
        onUpdated={handleDateChipsUpdate}
      />
    </div>
  )
}
